import React, { useEffect, useState } from 'react';
import TopBar from '@/components/TopBar';
import { useCity } from '@/context/CityContext';
import { getArrival } from '@/lib/api';
import { Car, Wifi, MessageSquareQuote, Copy, Check, Loader2, Compass } from 'lucide-react';
import { toast } from 'sonner';
import PlugCard from '@/components/essentials/PlugCard';
import TippingCard from '@/components/essentials/TippingCard';
import WaterCard from '@/components/essentials/WaterCard';
import EmergencyCard from '@/components/essentials/EmergencyCard';
import HolidaysCard from '@/components/essentials/HolidaysCard';
import VisaCard from '@/components/essentials/VisaCard';
import EmbassyCard from '@/components/essentials/EmbassyCard';
import ScamsCard from '@/components/essentials/ScamsCard';
import AirportCard from '@/components/essentials/AirportCard';

export default function Arrival() {
  const { city } = useCity();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(-1);

  useEffect(() => {
    let c = true;
    setLoading(true);
    getArrival(city.lat, city.lon, 'USD')
      .then((r) => { if (c) setData(r); })
      .catch(() => { if (c) toast.error('Arrival info failed'); })
      .finally(() => c && setLoading(false));
    return () => { c = false; };
  }, [city.lat, city.lon]);

  const copyPhrase = async (txt, i) => {
    try { await navigator.clipboard.writeText(txt); setCopiedIdx(i); setTimeout(() => setCopiedIdx(-1), 1200); } catch {}
  };

  return (
    <div data-testid="essentials-screen">
      <TopBar />
      <div className="px-5 pt-1">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#15291C] flex items-center justify-center">
            <Compass className="w-5 h-5 text-[var(--puvio-sun)]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-[var(--puvio-mint-deep)]">City essentials</p>
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="essentials-title">{city.name}{city.country ? `, ${city.country}` : ''}</h1>
          </div>
        </div>
      </div>

      <div className="px-5 pt-5 flex flex-col gap-4">
        {/* Survival pack — country-level static guidance */}
        <AirportCard cityName={city.name} countryCode={city.country_code} />
        <VisaCard destCountryCode={city.country_code} destCountryName={city.country} />
        <EmbassyCard destCountryCode={city.country_code} destCountryName={city.country} />
        <ScamsCard countryCode={city.country_code} countryName={city.country} />
        <PlugCard countryCode={city.country_code} countryName={city.country} />
        <WaterCard countryCode={city.country_code} countryName={city.country} />
        <EmergencyCard countryCode={city.country_code} countryName={city.country} />
        <TippingCard countryCode={city.country_code} countryName={city.country} />
        <HolidaysCard countryCode={city.country_code} countryName={city.country} />
        {/* Currency */}
        <div className="puvio-card p-5" data-testid="arrival-currency-card">
          <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Local currency</p>
          {loading ? (
            <div className="puvio-skeleton h-8 w-40 mt-3" />
          ) : data?.currency?.local ? (
            <>
              <div className="mt-1 flex items-end gap-2">
                <span className="text-3xl font-semibold tracking-tight">{data.currency.local}</span>
                <span className="text-sm text-[#4A5A50] mb-1.5">local currency</span>
              </div>
              {data.currency.preview && (
                <p className="text-sm text-[#4A5A50] mt-1" data-testid="arrival-currency-preview">{data.currency.preview}</p>
              )}
            </>
          ) : <p className="text-sm text-[#4A5A50] mt-2">Unavailable</p>}
        </div>

        {/* Taxi */}
        <div className="puvio-card p-5" data-testid="arrival-taxi-grid">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Taxi &amp; rideshare</p>
              <p className="text-sm text-[#4A5A50] mt-0.5">Apps that work here</p>
            </div>
            <div className="w-10 h-10 rounded-2xl bg-[#C4FF3D]/30 flex items-center justify-center"><Car className="w-5 h-5 text-[#15291C]" /></div>
          </div>
          {loading ? (
            <div className="mt-4 grid grid-cols-2 gap-2"> {[...Array(4)].map((_, i) => <div key={i} className="puvio-skeleton h-10" />)} </div>
          ) : (
            <div className="mt-4 grid grid-cols-2 gap-2">
              {(data?.taxi_apps || []).map((t, i) => (
                <div key={i} className="h-11 rounded-2xl bg-[#0F1A13]/05 border border-black/5 flex items-center justify-center text-sm font-medium" data-testid={`arrival-taxi-${i}`}>{t}</div>
              ))}
            </div>
          )}
        </div>

        {/* SIM */}
        <div className="puvio-card p-5" data-testid="arrival-sim-tips">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#C4FF3D]/30 flex items-center justify-center"><Wifi className="w-5 h-5 text-[#15291C]" /></div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">SIM &amp; connectivity</p>
              <p className="text-sm text-[#4A5A50] mt-0.5">Get online quickly</p>
            </div>
          </div>
          {loading ? (
            <div className="puvio-skeleton h-4 w-5/6 mt-3" />
          ) : (
            <p className="text-sm text-[#0F1A13] mt-3 leading-relaxed">{data?.sim_tip}</p>
          )}
        </div>

        {/* Phrases */}
        <div className="puvio-card p-5" data-testid="arrival-phrases-list">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#15291C] flex items-center justify-center"><MessageSquareQuote className="w-5 h-5 text-[#15291C]" /></div>
            <div>
              <p className="text-xs uppercase tracking-wider font-semibold text-[#15291C]">Local phrases</p>
              <p className="text-sm text-[#4A5A50] mt-0.5">{data?.language ? `In ${data.language}` : 'Essential starters'}</p>
            </div>
          </div>
          {loading ? (
            <div className="mt-3 flex flex-col gap-2"> {[...Array(4)].map((_, i) => <div key={i} className="puvio-skeleton h-12" />)} </div>
          ) : (
            <ul className="mt-4 flex flex-col gap-2">
              {(data?.phrases || []).map((p, i) => (
                <li key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-[#0F1A13]/05 border border-black/5" data-testid={`arrival-phrase-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-[#4A5A50]">{p.en}</p>
                    <p className="text-sm font-semibold text-[#0F1A13] truncate">{p.local}</p>
                    {p.pronunciation && <p className="text-[11px] text-[#7A8A80] italic truncate">{p.pronunciation}</p>}
                  </div>
                  <button type="button" onClick={() => copyPhrase(p.local, i)} className="puvio-pill puvio-pill-light text-xs puvio-focus" aria-label="Copy phrase" data-testid={`arrival-phrase-copy-${i}`}>
                    {copiedIdx === i ? <Check className="w-3.5 h-3.5 text-[#15291C]" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
